import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Dashboard.module.css';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';
import defaultAvatar from '../assets/DefaultIcon.png';
import PreferencesModal from '../components/PreferencesModal';
import ThemeSelector from '../components/ThemeSelector';
import Tooltip from '../components/Tooltip';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { 
  User, Rocket, Briefcase, Calendar, Settings, ScrollText, Coins, 
  BarChart3, Zap, Bell, Clock, CheckCircle, Activity, TrendingUp, HelpCircle, Lock, BookOpen 
} from 'lucide-react';
import OrgPointsBadge from '../components/OrgPointsBadge';
import LevelBadge from '../components/LevelBadge/LevelBadge';
import MyMissions from './MyMissions';
import { checkForOfflineRewards, storeOrgPoints, getStoredOrgPoints, getToastedTransactionIds, markTransactionsAsToasted } from '../utils/rewardNotifications';
import { getLevel, formatLevelBadge } from '../utils/levels';
import { ToastType } from '../context/ToastContext';
import { useToast } from '../context/ToastContext';
import FeatureDiscoveryWidget from '../components/FeatureDiscoveryWidget';
import { getProfileFrameClass } from '../utils/levelStyles';
import { CommendationsDisplay } from '../components/CommendationsDisplay';

// ✅ Add this here — not above imports
const getFullImageUrl = (url: string | null | undefined) =>
  url?.startsWith('http') ? url : `${process.env.REACT_APP_API_URL}${url}`;

const getTimeBasedGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const getTimeUntilEvent = (eventStart: string): string => {
  const now = new Date();
  const start = new Date(eventStart);
  const diffMs = start.getTime() - now.getTime();

  if (diffMs < 0) return 'Event has started';

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

interface EventDto {
  id: number;
  title: string;
  start: string;
  description?: string;
  role?: string;
  eventImageUrl?: string;
  discordChannelId?: string;
  discordMessageId?: string;
}

const Dashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { showMissionCompleteToast, queueToast, queueMultipleToasts } = useToast();

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(user?.username || '');
  const [gameUserName, setGameUserName] = useState(user?.gameUserName || '');
  const [discordName, setDiscordName]     = useState(user?.discordName   || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [orgPoints, setOrgPoints] = useState<number>(0);
  const [canAcceptMissions, setCanAcceptMissions] = useState<boolean>(false);
  // Level-based profile enhancements
  const [motto, setMotto] = useState<string>('');
  const [profileTheme, setProfileTheme] = useState<string>('');
  const [showEliteLogs, setShowEliteLogs] = useState<boolean>(false);
  const [paragonBio, setParagonBio] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');
  const [preferredPlaytimes, setPreferredPlaytimes] = useState<{
    [key: string]: { start: string; end: string; available: boolean }
  }>({});
  const [commendationsCount, setCommendationsCount] = useState<number>(0);
  const [orgJourney, setOrgJourney] = useState({
    interests: ['Exploration', 'Mining'],
    playstyle: 'Weekly Ops',
    squadPreference: 'Looking for a Squad',
    skills: 'Logistics, Mentoring, Fleet Tactics',
    primaryRole: '', // ← add
    secondaryRole: '', // ← add
    tertiaryRole: '' // ← add
  });
  
  const [showModal, setShowModal] = useState(false);
  const [showCodeOfConductModal, setShowCodeOfConductModal] = useState(false);
  const [positionInfo, setPositionInfo] = useState<any[]>([]);
  const [userEvents, setUserEvents] = useState<EventDto[]>([]);
  const [missionHistory, setMissionHistory] = useState<any[]>([]);
  const [activeMissions, setActiveMissions] = useState<any[]>([]);
  const [lastLogin, setLastLogin] = useState<string>('');
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [leadershipStats, setLeadershipStats] = useState({
    pendingApprovals: 0,
    totalMembers: 0,
    missionsCreated: 0,
    unreadMessages: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState({
    profile: true,
    orgPoints: true,
    preferences: true,
    positions: true,
    events: true,
    missions: true
  });
  const [gettingStartedChecklist, setGettingStartedChecklist] = useState({
    codeOfConduct: false,
    discordVerified: false,
    joinedRSI: false,
    discordRoles: false,
    profileComplete: false,
    departmentJoined: false,
    eventAttended: false,
    pointsEarned: false,
    essentialReading: false
  });

  const [requiredReadingProgress, setRequiredReadingProgress] = useState<{
    totalRequired: number;
    completed: number;
    articles: Array<{ id: number; slug: string; title: string; category: string; completed: boolean }>;
  }>({ totalRequired: 0, completed: 0, articles: [] });

  // Progressive Disclosure: Determine user's journey stage
  const navigate = useNavigate();
  const getUserStage = (): 'PROSPECT' | 'NEW_MEMBER' | 'ACTIVE_MEMBER' | 'LEADER' => {
    // Stage 1: Prospect (not yet a member)
    if (user?.role === 'Prospect' || !user?.flags?.includes('OrgMember')) {
      return 'PROSPECT';
    }

    // Stage 4: Leader/Admin (highest priority check)
    if (user?.role === 'Leader' || user?.role === 'Admin') {
      return 'LEADER';
    }

    // Stage 2: New Member (onboarding incomplete)
    const onboardingComplete = 
      gettingStartedChecklist.profileComplete &&
      gettingStartedChecklist.departmentJoined &&
      gettingStartedChecklist.eventAttended &&
      gettingStartedChecklist.pointsEarned &&
      gettingStartedChecklist.essentialReading;

    if (!onboardingComplete) {
      return 'NEW_MEMBER';
    }

    // Stage 3: Active Member (onboarding complete)
    return 'ACTIVE_MEMBER';
  };

  const userStage = getUserStage();

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    const formData = new FormData();
    formData.append('avatar', avatarFile);
  
    try {
      const res = await axios.post('/auth/user/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('Uploaded:', res.data.avatarUrl);
      await refreshUser(); // ✅ Reload user info
    } catch (err) {
      console.error('Avatar upload failed:', err);
    }
  };
  
  const handleSave = async () => {
    try {
      await axios.put('/auth/user/update-profile', { 
        username, 
        gameUserName, 
        discordName,
        motto,
        profileTheme,
        showEliteLogs,
        paragonBio,
        timezone: timezone || null,
        preferredPlaytimes: Object.keys(preferredPlaytimes).length > 0 ? JSON.stringify(preferredPlaytimes) : null
      });
      await refreshUser();
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await axios.get('/auth/user/profile');
        // Removed refreshUser() call to prevent infinite loop
        setUsername(res.data.username);
        setGameUserName(res.data.gameUserName);
        
        // Load level-based profile enhancements
        setMotto(res.data.motto || '');
        setProfileTheme(res.data.profileTheme || '');
        setShowEliteLogs(res.data.showEliteLogs || false);
        setParagonBio(res.data.paragonBio || '');
        setTimezone(res.data.timezone || '');
        
        // Parse preferred playtimes from JSON string
        if (res.data.preferredPlaytimes) {
          try {
            setPreferredPlaytimes(JSON.parse(res.data.preferredPlaytimes));
          } catch (e) {
            console.error('Failed to parse preferred playtimes:', e);
            setPreferredPlaytimes({});
          }
        }
        
        // Check mission access based on Level 1+ or CanAcceptMissions flag
        const userOrgPoints = res.data.orgPoints || 0;
        const hasFlag = res.data.flags?.includes('CanAcceptMissions');
        const isLevel1Plus = userOrgPoints >= 10;
        setCanAcceptMissions(isLevel1Plus || hasFlag);
      } catch (err) {
        console.error('Failed to load user profile', err);
      } finally {
        setLoadingStates(prev => ({ ...prev, profile: false }));
      }
    };

    const fetchOrgPoints = async () => {
      try {
        const res = await axios.get('/auth/user/org-points');
        const currentPoints = res.data.orgPoints || 0;
        setOrgPoints(currentPoints);
        
        // Check for offline rewards (points gained while user was logged out)
        const pointsGained = checkForOfflineRewards(currentPoints);
        console.log('=== TOAST DEBUG START ===');
        console.log('Current points:', currentPoints);
        console.log('Points gained:', pointsGained);
        
        if (pointsGained > 0) {
          // Fetch ONLY untoasted transactions from the server
          try {
            const transactionsRes = await axios.get('/auth/user/org-points/history?untoastedOnly=true');
            const untoastedTransactions = transactionsRes.data || [];
            
            console.log('Untoasted transactions from server:', untoastedTransactions.length);
            
            if (untoastedTransactions.length > 0) {
              // Queue individual toasts for each transaction
              const toastsToQueue = untoastedTransactions.slice(0, 10).map((t: any) => {
                // Check if this transaction caused a level up
                const oldLevel = getLevel(currentPoints - t.points).level;
                const newLevel = getLevel(currentPoints).level;
                const didLevelUp = newLevel > oldLevel;
                
                let message = t.reason || `Earned ${t.points} ORG Points`;
                if (t.missionTitle) {
                  message = `${t.reason}\n${t.missionTitle}`;
                }
                
                return {
                  message,
                  type: 'celebration' as ToastType,
                  options: {
                    duration: 6000,
                    points: t.points,
                    levelUp: didLevelUp,
                    newLevel: didLevelUp ? newLevel : undefined,
                    levelTitle: didLevelUp ? formatLevelBadge(newLevel) : undefined
                  }
                };
              });
              
              // Mark these transactions as toasted on the server
              const transactionIds = untoastedTransactions.slice(0, 10).map((t: any) => t.id);
              console.log('Marking as toasted on server:', transactionIds);
              
              try {
                await axios.post('/auth/user/org-points/mark-toasted', transactionIds);
                console.log('✅ Successfully marked on server');
              } catch (err) {
                console.error('Failed to mark as toasted on server:', err);
                // Continue anyway - toasts will still show
              }
              
              // Update stored points AFTER marking toasts
              storeOrgPoints(currentPoints);
              
              // Queue all toasts to show sequentially
              queueMultipleToasts(toastsToQueue);
              console.log('=== TOAST DEBUG END ===');
            } else {
              // No untoasted transactions but points increased - generic celebration
              storeOrgPoints(currentPoints);
              queueToast(
                `You earned ${pointsGained} ORG Points while you were away!`,
                'celebration',
                { duration: 6000, points: pointsGained }
              );
            }
          } catch (txErr) {
            console.error('Failed to fetch transactions for toast', txErr);
            // Fallback: show generic celebration if we can't fetch transactions
            storeOrgPoints(currentPoints);
            queueToast(
              `You earned ${pointsGained} ORG Points while you were away!`,
              'celebration',
              { duration: 6000, points: pointsGained }
            );
          }
        } else {
          // No offline rewards, just update stored points
          storeOrgPoints(currentPoints);
        }
      } catch (err) {
        console.error('Failed to load ORG points', err);
      } finally {
        setLoadingStates(prev => ({ ...prev, orgPoints: false }));
      }
    };
  
    const fetchPreferences = async () => {
      try {
        const res = await axios.get('/auth/user/preferences');
        const data = res.data || {
          interests: [],
          playstyle: '',
          squadPreference: '',
          skills: '',
          primaryRole: '',
          secondaryRole: '',
          tertiaryRole: ''
        };
        setOrgJourney(data);
      } catch (err) {
        console.error('Failed to load preferences', err);
        setOrgJourney({
          interests: [],
          playstyle: '',
          squadPreference: '',
          skills: '',
          primaryRole: '',
          secondaryRole: '',
          tertiaryRole: ''
        });
      } finally {
        setLoadingStates(prev => ({ ...prev, preferences: false }));
      }
    };
    const fetchPositions = async () => {
      try {
        const res = await axios.get('/auth/user/positions');
        setPositionInfo(res.data);
      } catch (err) {
        console.error('Failed to load positions', err);
      } finally {
        setLoadingStates(prev => ({ ...prev, positions: false }));
      }
    };
    const fetchUserEvents = async () => {
      try {
        const res = await axios.get('/events/my-rsvps');
        const mappedEvents = res.data.map((evt: any) => ({
          id: evt.Id || evt.id,
          title: evt.Title || evt.title,
          start: evt.Start || evt.start,
          description: evt.Description || evt.description,
          eventImageUrl: evt.EventImageUrl || evt.eventImageUrl,
          role: evt.Role || evt.role,
          discordChannelId: evt.DiscordChannelId || evt.discordChannelId,
          discordMessageId: evt.DiscordMessageId || evt.discordMessageId,
        }));
        setUserEvents(mappedEvents);
      } catch (err) {
        console.error('Failed to load RSVP\'d events', err);
      } finally {
        setLoadingStates(prev => ({ ...prev, events: false }));
      }
    };

    const fetchMissionHistory = async () => {
      try {
        const res = await axios.get('/missions/my');
        const completed = res.data.filter((m: any) => m.status === 'Completed');
        setMissionHistory(completed);
      } catch (err) {
        console.error('Failed to load mission history', err);
        setMissionHistory([]);
      } finally {
        setLoadingStates(prev => ({ ...prev, missions: false }));
      }
    };

    const fetchActiveMissions = async () => {
      try {
        const res = await axios.get('/missions/my');
        setActiveMissions(res.data.filter((m: any) => m.status === 'Accepted' || m.status === 'InProgress'));
      } catch (err) {
        console.error('Failed to load active missions', err);
        setActiveMissions([]);
      }
    };

    const fetchLastLogin = async () => {
      try {
        const res = await axios.get('/auth/user/profile');
        setLastLogin(res.data.lastLogin || new Date().toISOString());
      } catch (err) {
        console.error('Failed to load last login', err);
      }
    };

    const buildRecentActivity = () => {
      const activities = [];
      
      // Add recent events
      userEvents.slice(0, 2).forEach(event => {
        activities.push({
          type: 'event',
          title: `RSVP'd to ${event.title}`,
          time: new Date(event.start).toLocaleDateString(),
          icon: 'calendar'
        });
      });
      
      // Add recent missions
      if (missionHistory.length > 0) {
        activities.push({
          type: 'mission',
          title: `Completed mission`,
          time: new Date(missionHistory[0]?.completedAt || Date.now()).toLocaleDateString(),
          icon: 'check'
        });
      }
      
      // Add points earned
      if (orgPoints > 0) {
        activities.push({
          type: 'points',
          title: `Earned ORG Points`,
          time: 'Recently',
          icon: 'coins'
        });
      }
      
      setRecentActivity(activities.slice(0, 5));
    };

    fetchUserProfile();
    fetchOrgPoints();
    fetchPreferences();
    fetchPositions();
    fetchUserEvents();
    fetchMissionHistory();
    fetchActiveMissions();
    fetchLastLogin();
    
    // Fetch leadership stats for leaders/admins
    if (user?.role === 'Leader' || user?.role === 'Admin') {
      const fetchLeadershipStats = async () => {
        try {
          const [approvalsRes, membersRes, messagesRes] = await Promise.all([
            axios.get('/permissionrequests/pending-count').catch(() => ({ data: 0 })),
            axios.get('/auth/user/count').catch(() => ({ data: 0 })),
            axios.get('/leadershipmessages/stats').catch(() => ({ data: { unreadCount: 0 } }))
          ]);
          
          setLeadershipStats({
            pendingApprovals: approvalsRes.data || 0,
            totalMembers: membersRes.data || 0,
            missionsCreated: 0, // Could add endpoint for this
            unreadMessages: messagesRes.data?.unreadCount || 0
          });
        } catch (err) {
          console.error('Failed to fetch leadership stats:', err);
        }
      };
      fetchLeadershipStats();
    }
  }, []); // Run only once on mount to prevent infinite loop

  // Check if all data is loaded
  useEffect(() => {
    const allLoaded = Object.values(loadingStates).every(state => !state);
    if (allLoaded) {
      setIsLoading(false);
    }
  }, [loadingStates]);

  // Build recent activity when data changes
  useEffect(() => {
    if (userEvents.length > 0 || missionHistory.length > 0 || orgPoints > 0) {
      const activities = [];
      
      userEvents.slice(0, 2).forEach(event => {
        activities.push({
          type: 'event',
          title: `RSVP'd to ${event.title}`,
          time: new Date(event.start).toLocaleDateString(),
          icon: 'calendar'
        });
      });
      
      if (missionHistory.length > 0) {
        activities.push({
          type: 'mission',
          title: `Completed ${missionHistory[0]?.missionTitle || 'a mission'}`,
          time: new Date(missionHistory[0]?.completedAt || Date.now()).toLocaleDateString(),
          icon: 'check'
        });
      }
      
      if (orgPoints > 0) {
        activities.push({
          type: 'points',
          title: `Total ORG Points: ${orgPoints.toLocaleString()}`,
          time: 'Current',
          icon: 'coins'
        });
      }
      
      setRecentActivity(activities.slice(0, 5));
    }
  }, [userEvents, missionHistory, orgPoints]);

  // Separate useEffect to update checklist when user data changes
  useEffect(() => {
    // Load onboarding progress from localStorage (or could be from backend)
    const savedProgress = localStorage.getItem(`onboarding_${user?.id}`);
    const progress = savedProgress ? JSON.parse(savedProgress) : {};

    // Fetch required reading progress for all users
    const fetchReadingProgress = async () => {
      try {
        // Determine userType parameter based on user role and flags
        let userType = 'Member'; // default
        if (user?.role === 'Prospect' && !user?.flags?.includes('OrgMember')) {
          userType = 'Prospect';
        } else if (user?.role === 'Leader' || user?.role === 'Admin') {
          userType = 'Leader';
        }

        const res = await axios.get(`/knowledgebase/articles/required/progress?userType=${userType}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log('Reading progress response:', res.data);
        setRequiredReadingProgress(res.data);
        return res.data.totalRequired > 0 && res.data.completed === res.data.totalRequired;
      } catch (error) {
        console.error('Failed to fetch required reading progress:', error);
        return false;
      }
    };

    fetchReadingProgress().then(isReadingComplete => {
      const checklist = {
        codeOfConduct: progress.codeOfConduct || false,
        discordVerified: progress.discordVerified || false,
        joinedRSI: progress.joinedRSI || false,
        discordRoles: progress.discordRoles || false,
        profileComplete: !!(user?.gameUserName && user?.discordName),
        departmentJoined: !!(user?.primaryPosition || user?.secondaryPosition || user?.tertiaryPosition),
        eventAttended: userEvents.length > 0,
        pointsEarned: orgPoints > 0,
        essentialReading: isReadingComplete
      };
      console.log('=== ONBOARDING DEBUG ===');
      console.log('User flags:', user?.flags);
      console.log('Required reading progress:', requiredReadingProgress);
      console.log('Is reading complete:', isReadingComplete);
      console.log('Checklist state:', checklist);
      console.log('Should show Getting Started:', user?.flags?.includes('OrgMember') && (
        !checklist.profileComplete || 
        !checklist.departmentJoined || 
        !checklist.eventAttended || 
        !checklist.pointsEarned ||
        !checklist.essentialReading
      ));
      setGettingStartedChecklist(checklist);
    });
  }, [user, userEvents, orgPoints]);

  // Refetch reading progress when user returns to the page
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && user) {
        try {
          let userType = 'Member';
          if (user?.role === 'Prospect' && !user?.flags?.includes('OrgMember')) {
            userType = 'Prospect';
          } else if (user?.role === 'Leader' || user?.role === 'Admin') {
            userType = 'Leader';
          }

          const res = await axios.get(`/knowledgebase/articles/required/progress?userType=${userType}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          console.log('Refreshed reading progress:', res.data);
          setRequiredReadingProgress(res.data);
          
          // Update checklist with new reading completion status
          const isReadingComplete = res.data.totalRequired > 0 && res.data.completed === res.data.totalRequired;
          setGettingStartedChecklist(prev => ({
            ...prev,
            essentialReading: isReadingComplete
          }));
        } catch (error) {
          console.error('Failed to refresh reading progress:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const handleChecklistToggle = async (step: string) => {
    const newChecklist = { ...gettingStartedChecklist, [step]: !gettingStartedChecklist[step as keyof typeof gettingStartedChecklist] };
    setGettingStartedChecklist(newChecklist);
    
    // Save to localStorage
    if (user?.id) {
      const progress = {
        codeOfConduct: newChecklist.codeOfConduct,
        discordVerified: newChecklist.discordVerified,
        joinedRSI: newChecklist.joinedRSI
      };
      localStorage.setItem(`onboarding_${user.id}`, JSON.stringify(progress));
    }

    // If Prospect completes all 3 pre-member steps, call API to mark onboarding complete
    if (user?.role === 'Prospect' && 
        newChecklist.codeOfConduct && 
        newChecklist.discordVerified && 
        newChecklist.joinedRSI) {
      try {
        const response = await axios.post('/auth/user/complete-onboarding');
        alert(response.data.message || 'Onboarding completed! Your application will now be reviewed by leadership.');
        await refreshUser(); // Refresh user data
      } catch (error: any) {
        console.error('Failed to complete onboarding:', error);
        if (error.response?.data?.message) {
          alert(error.response.data.message);
        }
      }
    }
  };
  
  const getUserRole = (role?: string): string => {
    const validRoles = ['Admin', 'Leader', 'Member'];
    return validRoles.includes(role || '') ? role! : 'Member';
  };  
  
  const handleCancelRsvp = async (
    eventId: number,
    role?: string,
    channelId?: string,
    messageId?: string
  ) => {
    if (!role) {
      console.warn('Cannot cancel RSVP: missing role');
      alert('Missing role information. Cannot cancel RSVP.');
      return;
    }
  
    try {
      await axios.post('/events/rsvp', {
        eventId,
        username: user?.username,
        attending: false,
        role,
      });
  
      // Refetch user events to get updated list
      const res = await axios.get('/events/my-rsvps');
      setUserEvents(res.data);

      // Note: Discord bot notification is handled by the backend
  
    } catch (err) {
      console.error('Failed to cancel RSVP from dashboard:', err);
      alert('Failed to cancel RSVP.');
    }
  };
  
  const handleDeleteAccount = async () => {
    if (!window.confirm("This action is irreversible. Are you sure you want to delete your account?")) return;
  
    try {
      await axios.delete('/auth/user/delete');
      alert("Your account has been deleted.");
      window.location.href = '/'; // Or redirect to login/home page
    } catch (err) {
      console.error('Account deletion failed:', err);
      alert("Failed to delete account.");
    }
  };
  
  
  return (
    <div className={`${styles.dashboardContainer} ${profileTheme ? styles[`theme-${profileTheme}`] : ''}`}>
      <div className={styles.content}>
        {/* Redesigned Profile Header */}
        <div className={styles.profileHeader}>
          <div className={`${styles.profileHeaderLeft} ${getProfileFrameClass(getLevel(orgPoints).level)}`}>
            <img
              src={getFullImageUrl(user.avatarUrl) || defaultAvatar}
              alt="Avatar"
              className={styles.profileAvatar}
              onError={(e) => {
                (e.target as HTMLImageElement).src = defaultAvatar;
              }}
            />
            <div className={styles.profileInfo}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h1 className={styles.profileName}>{getTimeBasedGreeting()}, {user?.username}!</h1>
                <LevelBadge level={getLevel(orgPoints).level} size="medium" />
              </div>
              <p className={styles.profileRole}>
                <span style={{ opacity: 0.7, fontSize: '0.85em', marginRight: '0.5rem' }}>Access Level:</span>
                {user?.role || 'Member'}
              </p>
              {user?.primaryPosition?.title ? (
                <p className={styles.profileWelcome}>
                  {user.primaryPosition.title} • {user.primaryPosition.department}
                  {user?.joinDate && (
                    <span style={{ marginLeft: '1rem', opacity: 0.7, fontSize: '0.9em' }}>
                      • Member since {new Date(user.joinDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </p>
              ) : (
                !gettingStartedChecklist.codeOfConduct ||
                !gettingStartedChecklist.discordVerified ||
                !gettingStartedChecklist.joinedRSI ||
                !gettingStartedChecklist.discordRoles ||
                !gettingStartedChecklist.profileComplete || 
                !gettingStartedChecklist.departmentJoined || 
                !gettingStartedChecklist.eventAttended || 
                !gettingStartedChecklist.pointsEarned
              ) && (
                <p className={styles.profileWelcome}>
                  Welcome to TFIC! Complete your getting started checklist below to begin your journey! 🚀
                </p>
              )}
              <div style={{ marginTop: 8 }}>
                <OrgPointsBadge points={orgPoints} />
              </div>
            </div>
          </div>
          
          <div className={styles.profileHeaderRight}>
            <div className={styles.orgPointsDisplay}>
              <div className={styles.orgPointsIcon}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="2" fill="currentColor"/>
                  <path d="M12 2L14 4L12 6L10 4L12 2Z" fill="currentColor"/>
                  <path d="M12 22L14 20L12 18L10 20L12 22Z" fill="currentColor"/>
                  <path d="M22 12L20 14L18 12L20 10L22 12Z" fill="currentColor"/>
                  <path d="M2 12L4 14L6 12L4 10L2 12Z" fill="currentColor"/>
                </svg>
              </div>
              <div className={styles.orgPointsInfo}>
                <span className={styles.orgPointsLabel}>
                  ORG POINTS
                  <Tooltip 
                    content="Earn ORG Points by completing missions, attending events, and contributing to the organization. Points unlock levels, badges, and exclusive perks!"
                    position="bottom"
                  >
                    <HelpCircle size={14} style={{ marginLeft: '0.4rem', opacity: 0.7, cursor: 'help' }} />
                  </Tooltip>
                </span>
                <span className={styles.orgPointsValue}>{orgPoints.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={styles.tabNavigation}>
          <button
            className={`${styles.tabButton} ${activeTab === 'overview' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
            </svg>
            Overview
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'profile' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <User size={18} /> Profile
          </button>
          
          {/* My Org Journey - Hidden for Prospects */}
          {userStage !== 'PROSPECT' ? (
            <button
              className={`${styles.tabButton} ${activeTab === 'journey' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('journey')}
            >
              <Rocket size={18} /> My Org Journey
            </button>
          ) : (
            <Tooltip content="Complete pre-member onboarding to unlock" position="bottom">
              <button
                className={`${styles.tabButton} ${styles.tabButtonDisabled}`}
                disabled
              >
                <Lock size={16} style={{ marginRight: '0.25rem' }} />
                <Rocket size={18} /> My Org Journey
              </button>
            </Tooltip>
          )}

          {/* My Positions - Hidden for Prospects */}
          {userStage !== 'PROSPECT' ? (
            <button
              className={`${styles.tabButton} ${activeTab === 'positions' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('positions')}
            >
              <Briefcase size={18} /> My Positions
            </button>
          ) : (
            <Tooltip content="Complete pre-member onboarding to unlock" position="bottom">
              <button
                className={`${styles.tabButton} ${styles.tabButtonDisabled}`}
                disabled
              >
                <Lock size={16} style={{ marginRight: '0.25rem' }} />
                <Briefcase size={18} /> My Positions
              </button>
            </Tooltip>
          )}

          {/* My Events - Hidden for Prospects */}
          {userStage !== 'PROSPECT' ? (
            <button
              className={`${styles.tabButton} ${activeTab === 'events' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('events')}
            >
              <Calendar size={18} /> My Events
            </button>
          ) : (
            <Tooltip content="Complete pre-member onboarding to unlock" position="bottom">
              <button
                className={`${styles.tabButton} ${styles.tabButtonDisabled}`}
                disabled
              >
                <Lock size={16} style={{ marginRight: '0.25rem' }} />
                <Calendar size={18} /> My Events
              </button>
            </Tooltip>
          )}

          {/* Mission History - Level 1+ only */}
          {canAcceptMissions ? (
            <button
              className={`${styles.tabButton} ${activeTab === 'missions' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('missions')}
            >
              <ScrollText size={18} /> Mission History
            </button>
          ) : (
            <Tooltip content="Reach Level 1 (10 ORG Points) to unlock missions" position="bottom">
              <button
                className={`${styles.tabButton} ${styles.tabButtonDisabled}`}
                disabled
              >
                <Lock size={16} style={{ marginRight: '0.25rem' }} />
                <ScrollText size={18} /> Mission History
              </button>
            </Tooltip>
          )}

          <button
            className={`${styles.tabButton} ${activeTab === 'settings' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={18} /> My Settings
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'overview' && (
            <div className={styles.overviewContainer}>
              
              {/* Stage Progress Banner - Shows user where they are in the journey */}
              {userStage === 'PROSPECT' && (
                <div className={styles.stageBanner}>
                  <div className={styles.stageBannerIcon}>
                    <Rocket size={24} />
                  </div>
                  <div className={styles.stageBannerContent}>
                    <h4 className={styles.stageBannerTitle}>Welcome, Prospect!</h4>
                    <p className={styles.stageBannerText}>
                      Complete the pre-member onboarding below to request membership and unlock more features.
                    </p>
                  </div>
                </div>
              )}
              
              {userStage === 'NEW_MEMBER' && (
                <div className={styles.stageBanner}>
                  <div className={styles.stageBannerIcon}>
                    <CheckCircle size={24} />
                  </div>
                  <div className={styles.stageBannerContent}>
                    <h4 className={styles.stageBannerTitle}>You're Now a Member!</h4>
                    <p className={styles.stageBannerText}>
                      Complete your member onboarding to unlock missions, participate in events, and access all features.
                    </p>
                  </div>
                </div>
              )}

              {/* ============================================
                  ONBOARDING & GUIDANCE SECTION (TOP)
                  ============================================ */}
              
              {/* Pre-Member Onboarding Checklist (for Prospects) */}
              {user?.role === 'Prospect' && (
                !gettingStartedChecklist.codeOfConduct ||
                !gettingStartedChecklist.discordVerified ||
                !gettingStartedChecklist.joinedRSI ||
                !gettingStartedChecklist.essentialReading
              ) && (
                <div className={styles.prominentChecklist}>
                  <div className={styles.checklistHeader}>
                    <div className={styles.checklistHeaderLeft}>
                      <Rocket size={24} className={styles.checklistHeaderIcon} />
                      <div>
                        <h3 className={styles.checklistHeaderTitle}>Pre-Member Onboarding</h3>
                        <p className={styles.checklistHeaderSubtitle}>Complete these external steps to request membership</p>
                      </div>
                    </div>
                    <div className={styles.checklistProgress}>
                      <span className={styles.checklistProgressText}>
                        {[gettingStartedChecklist.codeOfConduct, gettingStartedChecklist.discordVerified, gettingStartedChecklist.joinedRSI, gettingStartedChecklist.essentialReading].filter(Boolean).length} of 4 completed
                      </span>
                      <div className={styles.checklistProgressBar}>
                        <div 
                          className={styles.checklistProgressFill}
                          style={{ width: `${([gettingStartedChecklist.codeOfConduct, gettingStartedChecklist.discordVerified, gettingStartedChecklist.joinedRSI, gettingStartedChecklist.essentialReading].filter(Boolean).length / 4) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.checklistItemsContainer}>
                    {/* Step 1: Code of Conduct */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.codeOfConduct ? styles.prominentChecklistItemComplete : ''}`}>
                      <div 
                        className={styles.prominentCheckbox}
                        onClick={() => handleChecklistToggle('codeOfConduct')}
                        style={{ cursor: 'pointer' }}
                      >
                        {gettingStartedChecklist.codeOfConduct ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>Read Code of Conduct 🛡️</span>
                        <span className={styles.prominentChecklistHint}>Understand our community guidelines</span>
                      </div>
                      {!gettingStartedChecklist.codeOfConduct && (
                        <button 
                          className={styles.prominentChecklistAction}
                          onClick={() => setShowCodeOfConductModal(true)}
                        >
                          View Now
                        </button>
                      )}
                    </div>

                    {/* Step 2: Verify Discord */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.discordVerified ? styles.prominentChecklistItemComplete : ''}`}>
                      <div 
                        className={styles.prominentCheckbox}
                        onClick={() => handleChecklistToggle('discordVerified')}
                        style={{ cursor: 'pointer' }}
                      >
                        {gettingStartedChecklist.discordVerified ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>Verify Discord Account</span>
                        <span className={styles.prominentChecklistHint}>Complete Discord verification</span>
                      </div>
                      {!gettingStartedChecklist.discordVerified && (
                        <a 
                          href="https://discord.com/channels/1297670541846773922/1298346598673289216" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.prominentChecklistAction}
                          onClick={() => handleChecklistToggle('discordVerified')}
                        >
                          Verify Now
                        </a>
                      )}
                    </div>

                    {/* Step 3: Join RSI Org */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.joinedRSI ? styles.prominentChecklistItemComplete : ''}`}>
                      <div 
                        className={styles.prominentCheckbox}
                        onClick={() => handleChecklistToggle('joinedRSI')}
                        style={{ cursor: 'pointer' }}
                      >
                        {gettingStartedChecklist.joinedRSI ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>Join TFIC on RSI Website</span>
                        <span className={styles.prominentChecklistHint}>Officially join the organization</span>
                      </div>
                      {!gettingStartedChecklist.joinedRSI && (
                        <a 
                          href="https://robertsspaceindustries.com/en/orgs/MASTERLS" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.prominentChecklistAction}
                          onClick={() => handleChecklistToggle('joinedRSI')}
                        >
                          Join Now
                        </a>
                      )}
                    </div>

                    {/* Step 4: Required Reading for Prospects */}
                    {requiredReadingProgress && requiredReadingProgress.totalRequired > 0 && (
                      <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.essentialReading ? styles.prominentChecklistItemComplete : ''}`}>
                        <div className={styles.prominentCheckbox}>
                          {gettingStartedChecklist.essentialReading ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                        </div>
                        <div className={styles.prominentChecklistContent}>
                          <span className={styles.prominentChecklistTitle}>Read Essential Articles ({requiredReadingProgress.completed} of {requiredReadingProgress.totalRequired})</span>
                          <span className={styles.prominentChecklistHint}>Complete required reading before membership</span>
                          {requiredReadingProgress.articles.length > 0 && (
                            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '4px', borderLeft: '2px solid var(--border-color, #444)' }}>
                              {requiredReadingProgress.articles.map((article) => (
                                <a
                                  key={article.id}
                                  href={`/knowledge/${article.slug}`}
                                  style={{
                                    fontSize: '0.9rem',
                                    color: article.completed ? 'var(--success-color, #4CAF50)' : 'var(--text-primary)',
                                    textDecoration: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg, rgba(255,255,255,0.05))'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <span style={{ fontSize: '1.1rem', minWidth: '16px' }}>{article.completed ? '✓' : '○'}</span>
                                  <span style={{ opacity: article.completed ? 0.7 : 1, textDecoration: article.completed ? 'line-through' : 'none' }}>{article.title}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pending Approval Message (for Prospects who completed onboarding) */}
              {user?.role === 'Prospect' && 
               gettingStartedChecklist.codeOfConduct &&
               gettingStartedChecklist.discordVerified &&
               gettingStartedChecklist.joinedRSI &&
               gettingStartedChecklist.essentialReading && (
                <div className={styles.prominentChecklist + ' ' + styles.pendingApprovalCard}>
                  <div className={styles.pendingApprovalContent}>
                    <div className={styles.pendingIcon}>
                      <Clock size={64} />
                    </div>
                    <h3 className={styles.pendingTitle}>⏳ Membership Request Pending</h3>
                    <p className={styles.pendingDescription}>
                      You've completed all onboarding steps. Our leadership team will review your application and verify:
                    </p>
                    <ul className={styles.verificationList}>
                      <li>✓ Code of Conduct acceptance</li>
                      <li>✓ Discord account verification</li>
                      <li>✓ MASTERLS organization membership on RSI</li>
                    </ul>
                    <div className={styles.pendingNote}>
                      <strong>What's next?</strong> Once approved, you'll receive full member access and can start completing your Getting Started checklist to dive deeper into TFIC activities!
                    </div>
                  </div>
                </div>
              )}

              {/* Getting Started Checklist (for Members with OrgMember flag) */}
              {user?.flags?.includes('OrgMember') && (
                !gettingStartedChecklist.profileComplete || 
                !gettingStartedChecklist.departmentJoined || 
                !gettingStartedChecklist.eventAttended || 
                !gettingStartedChecklist.pointsEarned
              ) && (
                <div className={styles.prominentChecklist}>
                  <div className={styles.checklistHeader}>
                    <div className={styles.checklistHeaderLeft}>
                      <Zap size={24} className={styles.checklistHeaderIcon} />
                      <div>
                        <h3 className={styles.checklistHeaderTitle}>Getting Started with TFIC</h3>
                        <p className={styles.checklistHeaderSubtitle}>Get involved in our organization and earn your first rewards</p>
                      </div>
                    </div>
                    <div className={styles.checklistProgress}>
                      <span className={styles.checklistProgressText}>
                        {[gettingStartedChecklist.profileComplete, gettingStartedChecklist.departmentJoined, gettingStartedChecklist.eventAttended, gettingStartedChecklist.pointsEarned].filter(Boolean).length} of 4 completed
                      </span>
                      <div className={styles.checklistProgressBar}>
                        <div 
                          className={styles.checklistProgressFill}
                          style={{ width: `${([gettingStartedChecklist.profileComplete, gettingStartedChecklist.departmentJoined, gettingStartedChecklist.eventAttended, gettingStartedChecklist.pointsEarned].filter(Boolean).length / 4) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.welcomeMessageBanner}>
                    <h4>Welcome to The Frontier Initiative Corporation! 🎉</h4>
                    <p>
                      Congratulations on becoming a full member of TFIC! We're excited to have you as part of our growing community. 
                      Complete the steps below to get the most out of your membership.
                    </p>
                  </div>
                  <div className={styles.checklistItemsContainer}>
                    {/* Step 1: Complete Profile */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.profileComplete ? styles.prominentChecklistItemComplete : ''}`}>
                      <div className={styles.prominentCheckbox}>
                        {gettingStartedChecklist.profileComplete ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>Complete Your Profile</span>
                        <span className={styles.prominentChecklistHint}>Add your game name and Discord • Earn 15 points</span>
                      </div>
                      {!gettingStartedChecklist.profileComplete && (
                        <button className={styles.prominentChecklistAction} onClick={() => setActiveTab('profile')}>
                          Complete Now
                        </button>
                      )}
                    </div>

                    {/* Step 2: Join Department */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.departmentJoined ? styles.prominentChecklistItemComplete : ''}`}>
                      <div className={styles.prominentCheckbox}>
                        {gettingStartedChecklist.departmentJoined ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>Join a Department</span>
                        <span className={styles.prominentChecklistHint}>Find your place in the org • Earn 25 points</span>
                      </div>
                      {!gettingStartedChecklist.departmentJoined && (
                        <button className={styles.prominentChecklistAction} onClick={() => window.location.href = '/departments'}>
                          Browse Departments
                        </button>
                      )}
                    </div>

                    {/* Step 3: RSVP Event */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.eventAttended ? styles.prominentChecklistItemComplete : ''}`}>
                      <div className={styles.prominentCheckbox}>
                        {gettingStartedChecklist.eventAttended ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>RSVP to an Event</span>
                        <span className={styles.prominentChecklistHint}>Join us for operations • Build your reputation</span>
                      </div>
                      {!gettingStartedChecklist.eventAttended && (
                        <button className={styles.prominentChecklistAction} onClick={() => window.location.href = '/events'}>
                          View Events
                        </button>
                      )}
                    </div>

                    {/* Step 4: Earn Points */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.pointsEarned ? styles.prominentChecklistItemComplete : ''}`}>
                      <div className={styles.prominentCheckbox}>
                        {gettingStartedChecklist.pointsEarned ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>Earn Your First ORG Points</span>
                        <span className={styles.prominentChecklistHint}>Complete missions or attend events • Start your journey</span>
                      </div>
                      {!gettingStartedChecklist.pointsEarned && (
                        <button className={styles.prominentChecklistAction} onClick={() => window.location.href = '/missions'}>
                          Browse Missions
                        </button>
                      )}
                    </div>

                    {/* Step 5: Essential Reading */}
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.essentialReading ? styles.prominentChecklistItemComplete : ''}`}>
                      <div className={styles.prominentCheckbox}>
                        {gettingStartedChecklist.essentialReading ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>
                          Read Essential Articles ({requiredReadingProgress.completed} of {requiredReadingProgress.totalRequired})
                        </span>
                        <span className={styles.prominentChecklistHint}>Learn key concepts and procedures • Build your knowledge</span>
                        {requiredReadingProgress.articles.length > 0 && (
                          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '4px', borderLeft: '2px solid var(--border-color, #444)' }}>
                            {requiredReadingProgress.articles.map((article) => (
                              <a
                                key={article.id}
                                href={`/knowledge/${article.slug}`}
                                style={{
                                  fontSize: '0.9rem',
                                  color: article.completed ? 'var(--success-color, #4CAF50)' : 'var(--text-primary)',
                                  textDecoration: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg, rgba(255,255,255,0.05))'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontSize: '1.1rem', minWidth: '16px' }}>{article.completed ? '✓' : '○'}</span>
                                <span style={{ opacity: article.completed ? 0.7 : 1, textDecoration: article.completed ? 'line-through' : 'none' }}>{article.title}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Standalone New Required Reading Checklist (appears independently for members with incomplete required reading) */}
              {user?.flags?.includes('OrgMember') && 
               requiredReadingProgress && 
               requiredReadingProgress.totalRequired > 0 && 
               !gettingStartedChecklist.essentialReading && 
               gettingStartedChecklist.profileComplete && 
               gettingStartedChecklist.departmentJoined && 
               gettingStartedChecklist.eventAttended && 
               gettingStartedChecklist.pointsEarned && (
                <div className={styles.prominentChecklist}>
                  <div className={styles.checklistHeader}>
                    <div className={styles.checklistHeaderLeft}>
                      <BookOpen size={24} className={styles.checklistHeaderIcon} />
                      <div>
                        <h3 className={styles.checklistHeaderTitle}>New Required Reading</h3>
                        <p className={styles.checklistHeaderSubtitle}>Complete these essential articles to stay up to date</p>
                      </div>
                    </div>
                    <div className={styles.checklistProgress}>
                      <span className={styles.checklistProgressText}>
                        {requiredReadingProgress.completed} of {requiredReadingProgress.totalRequired} completed
                      </span>
                      <div className={styles.checklistProgressBar}>
                        <div 
                          className={styles.checklistProgressFill}
                          style={{ width: `${(requiredReadingProgress.completed / requiredReadingProgress.totalRequired) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.checklistItemsContainer}>
                    <div className={`${styles.prominentChecklistItem} ${gettingStartedChecklist.essentialReading ? styles.prominentChecklistItemComplete : ''}`}>
                      <div className={styles.prominentCheckbox}>
                        {gettingStartedChecklist.essentialReading ? <CheckCircle size={24} /> : <div className={styles.emptyCheckbox}></div>}
                      </div>
                      <div className={styles.prominentChecklistContent}>
                        <span className={styles.prominentChecklistTitle}>
                          Read Required Articles ({requiredReadingProgress.completed} of {requiredReadingProgress.totalRequired})
                        </span>
                        <span className={styles.prominentChecklistHint}>Stay informed with the latest essential information</span>
                        {requiredReadingProgress.articles.filter(a => !a.completed).length > 0 && (
                          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '4px', borderLeft: '2px solid var(--border-color, #444)' }}>
                            {requiredReadingProgress.articles.filter(article => !article.completed).map((article) => (
                              <a
                                key={article.id}
                                href={`/knowledge/${article.slug}`}
                                style={{
                                  fontSize: '0.9rem',
                                  color: 'var(--text-primary)',
                                  textDecoration: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg, rgba(255,255,255,0.05))'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontSize: '1.1rem', minWidth: '16px' }}>○</span>
                                <span>{article.title}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Continue Your Journey - Post-Checklist Recommendations */}
              {user?.flags?.includes('OrgMember') && 
               gettingStartedChecklist.profileComplete && 
               gettingStartedChecklist.departmentJoined && 
               gettingStartedChecklist.eventAttended && 
               gettingStartedChecklist.pointsEarned &&
               gettingStartedChecklist.essentialReading && (
                <div className={styles.journeySection}>
                  <div className={styles.journeySectionHeader}>
                    <Rocket size={28} className={styles.journeyIcon} />
                    <div>
                      <h2 className={styles.journeyTitle}>Continue Your Journey</h2>
                      <p className={styles.journeySubtitle}>You've completed onboarding! Here are some ways to grow within TFIC</p>
                    </div>
                  </div>

                  <div className={styles.opportunityCards}>
                    {/* Browse Missions Card */}
                    {activeMissions.length === 0 && (
                      <div className={styles.opportunityCard} onClick={() => window.location.href = '/missions'}>
                        <div className={styles.opportunityIcon}>
                          <ScrollText size={32} />
                        </div>
                        <h3 className={styles.opportunityTitle}>Accept Your Next Mission</h3>
                        <p className={styles.opportunityDescription}>
                          Browse available missions and start earning rewards. Each completed mission builds your reputation.
                        </p>
                        <button className={styles.opportunityButton}>Browse Missions →</button>
                      </div>
                    )}

                    {/* Upcoming Events Card */}
                    {userEvents.length < 3 && (
                      <div className={styles.opportunityCard} onClick={() => window.location.href = '/events'}>
                        <div className={styles.opportunityIcon}>
                          <Calendar size={32} />
                        </div>
                        <h3 className={styles.opportunityTitle}>Join Upcoming Events</h3>
                        <p className={styles.opportunityDescription}>
                          Participate in org operations and events to build connections and earn recognition.
                        </p>
                        <button className={styles.opportunityButton}>View Events →</button>
                      </div>
                    )}

                    {/* Level Up Card */}
                    <div className={styles.opportunityCard} onClick={() => window.location.href = '/general/org-points'}>
                      <div className={styles.opportunityIcon}>
                        <TrendingUp size={32} />
                      </div>
                      <h3 className={styles.opportunityTitle}>Advance Your Rank</h3>
                      <p className={styles.opportunityDescription}>
                        Keep earning ORG Points to unlock new levels, badges, and exclusive perks within the organization.
                      </p>
                      <button className={styles.opportunityButton}>View Levels →</button>
                    </div>

                    {/* Secondary Position Card */}
                    {!user?.secondaryPositionId && (
                      <div className={styles.opportunityCard} onClick={() => window.location.href = '/departments'}>
                        <div className={styles.opportunityIcon}>
                          <Briefcase size={32} />
                        </div>
                        <h3 className={styles.opportunityTitle}>Expand Your Role</h3>
                        <p className={styles.opportunityDescription}>
                          Apply for a secondary position to diversify your contributions and impact multiple departments.
                        </p>
                        <button className={styles.opportunityButton}>Explore Positions →</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ============================================
                  DASHBOARD WIDGETS SECTION (BELOW)
                  ============================================ */}
              
              {/* Widgets Grid - Adaptive based on user stage */}
              <div className={styles.widgetsGrid}>
                
                {/* PROSPECT STAGE: Minimal widgets, focus on onboarding */}
                {userStage === 'PROSPECT' && (
                  <>
                    {/* Quick Actions Widget - Personal only */}
                    <div className={styles.widget}>
                      <div className={styles.widgetHeader}>
                        <Zap size={18} style={{ marginRight: '0.5rem' }} />
                        <h3 className={styles.widgetTitle}>Quick Actions</h3>
                      </div>
                      <div className={styles.widgetContent}>
                        <div className={styles.quickActionsGroup}>
                          <p className={styles.quickActionsGroupLabel}>Get Started</p>
                          <div className={styles.quickActionsList}>
                            <button className={styles.quickActionButton} onClick={() => setActiveTab('profile')}>
                              <User size={20} />
                              <span>Complete Profile</span>
                            </button>
                            <button className={styles.quickActionButton} onClick={() => setShowModal(true)}>
                              <Rocket size={20} />
                              <span>Set Preferences</span>
                            </button>
                            <button className={styles.quickActionButton} onClick={() => setActiveTab('settings')}>
                              <Settings size={20} />
                              <span>Settings</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* NEW_MEMBER STAGE: Onboarding-focused widgets */}
                {userStage === 'NEW_MEMBER' && (
                  <>
                    {/* Quick Actions Widget */}
                    <div className={styles.widget}>
                      <div className={styles.widgetHeader}>
                        <Zap size={18} style={{ marginRight: '0.5rem' }} />
                        <h3 className={styles.widgetTitle}>Quick Actions</h3>
                      </div>
                      <div className={styles.widgetContent}>
                        <div className={styles.quickActionsGroup}>
                          <p className={styles.quickActionsGroupLabel}>Organization</p>
                          <div className={styles.quickActionsList}>
                            <button className={styles.quickActionButton} onClick={() => window.location.href = '/events'}>
                              <Calendar size={20} />
                              <span>View Events</span>
                            </button>
                            <button className={styles.quickActionButton} onClick={() => window.location.href = '/departments'}>
                              <Briefcase size={20} />
                              <span>Join Department</span>
                            </button>
                          </div>
                        </div>
                        <div className={styles.quickActionsGroup}>
                          <p className={styles.quickActionsGroupLabel}>Personal</p>
                          <div className={styles.quickActionsList}>
                            <button className={styles.quickActionButton} onClick={() => setActiveTab('profile')}>
                              <User size={20} />
                              <span>Edit Profile</span>
                            </button>
                            <button className={styles.quickActionButton} onClick={() => setShowModal(true)}>
                              <Rocket size={20} />
                              <span>Preferences</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feature Discovery Widget */}
                    <FeatureDiscoveryWidget 
                      orgPoints={orgPoints}
                      userFlags={user?.flags || []}
                      userRole={user?.role || ''}
                    />

                    {/* Upcoming Events Widget */}
                    <div className={styles.widget}>
                      <div className={styles.widgetHeader}>
                        <Calendar size={18} style={{ marginRight: '0.5rem' }} />
                        <h3 className={styles.widgetTitle}>Upcoming Events</h3>
                      </div>
                      <div className={styles.widgetContent}>
                        {userEvents.length === 0 ? (
                          <p className={styles.emptyState}>No upcoming events. Check the Events page to RSVP!</p>
                        ) : (
                          <div className={styles.activityList}>
                            {userEvents.slice(0, 3).map((event) => (
                              <div key={event.id} className={styles.activityItem}>
                                <Calendar size={18} className={styles.activityIcon} />
                                <div className={styles.activityDetails}>
                                  <span className={styles.activityTitle}>{event.title}</span>
                                  <span className={styles.activityTime}>
                                    {new Date(event.start).toLocaleDateString()} • {getTimeUntilEvent(event.start)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* ACTIVE_MEMBER & LEADER STAGE: Full dashboard */}
                {(userStage === 'ACTIVE_MEMBER' || userStage === 'LEADER') && (
                  <>
                {/* Quick Stats Widget */}
                <div className={styles.widget}>
                  <div className={styles.widgetHeader}>
                    <BarChart3 size={18} style={{ marginRight: '0.5rem' }} />
                    <h3 className={styles.widgetTitle}>Quick Stats</h3>
                    <Tooltip 
                      content="A quick overview of your key metrics and activities within the organization."
                      position="bottom"
                    >
                      <HelpCircle size={14} style={{ marginLeft: '0.4rem', opacity: 0.6, cursor: 'help' }} />
                    </Tooltip>
                  </div>
                  <div className={styles.widgetContent}>
                    {isLoading ? (
                      <>
                        <LoadingSkeleton variant="rectangular" height="60px" count={3} />
                      </>
                    ) : (user?.role === 'Leader' || user?.role === 'Admin') ? (
                      <>
                        {/* Leadership Stats */}
                        <div 
                          className={styles.statItem + ' ' + styles.statItemClickable}
                          onClick={() => window.location.href = '/leader-dashboard/permission-requests'}
                          style={{ cursor: 'pointer' }}
                        >
                          <Bell className={styles.statIcon} size={24} />
                          <div className={styles.statInfo}>
                            <span className={styles.statLabel}>Pending Approvals</span>
                            <span className={styles.statValue}>
                              {leadershipStats.pendingApprovals > 0 ? (
                                <span style={{ color: '#ff4444', fontWeight: 'bold' }}>{leadershipStats.pendingApprovals}</span>
                              ) : (
                                <span className={styles.emptyStatHint}>All caught up!</span>
                              )}
                            </span>
                          </div>
                          <div className={styles.statAction}>Review →</div>
                        </div>
                        <div 
                          className={styles.statItem + ' ' + styles.statItemClickable}
                          onClick={() => window.location.href = '/user-management'}
                          style={{ cursor: 'pointer' }}
                        >
                          <User className={styles.statIcon} size={24} />
                          <div className={styles.statInfo}>
                            <span className={styles.statLabel}>Total Members</span>
                            <span className={styles.statValue}>{leadershipStats.totalMembers}</span>
                          </div>
                          <div className={styles.statAction}>Manage →</div>
                        </div>
                        {user?.positionTier === 4 && (
                          <div 
                            className={styles.statItem + ' ' + styles.statItemClickable}
                            onClick={() => window.location.href = '/leadership-inbox'}
                            style={{ cursor: 'pointer' }}
                          >
                            <Bell className={styles.statIcon} size={24} />
                            <div className={styles.statInfo}>
                              <span className={styles.statLabel}>Unread Messages</span>
                              <span className={styles.statValue}>
                                {leadershipStats.unreadMessages > 0 ? (
                                  <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{leadershipStats.unreadMessages}</span>
                                ) : (
                                  <span className={styles.emptyStatHint}>Inbox clear!</span>
                                )}
                              </span>
                            </div>
                            <div className={styles.statAction}>Open Inbox →</div>
                          </div>
                        )}
                      </>
                    ) : null}
                    
                    {/* Standard Stats (shown to everyone) */}
                    <div 
                      className={styles.statItem + ' ' + styles.statItemClickable}
                      onClick={() => window.location.href = '/org-points/history'}
                      style={{ cursor: 'pointer' }}
                    >
                      <Coins className={styles.statIcon} size={24} />
                      <div className={styles.statInfo}>
                        <span className={styles.statLabel}>ORG Points</span>
                        <span className={styles.statValue}>{orgPoints > 0 ? orgPoints.toLocaleString() : <span className={styles.emptyStatHint}>Start earning!</span>}</span>
                      </div>
                      <div className={styles.statAction}>View History →</div>
                    </div>
                    <div className={styles.statItem}>
                      <Activity className={styles.statIcon} size={24} />
                      <div className={styles.statInfo}>
                        <span className={styles.statLabel}>Active Missions</span>
                        <span className={styles.statValue}>{activeMissions.length > 0 ? activeMissions.length : <span className={styles.emptyStatHint}>Browse missions!</span>}</span>
                      </div>
                    </div>
                    <div className={styles.statItem}>
                      <CheckCircle className={styles.statIcon} size={24} />
                      <div className={styles.statInfo}>
                        <span className={styles.statLabel}>Missions Completed</span>
                        <span className={styles.statValue}>{missionHistory.length > 0 ? missionHistory.length : <span className={styles.emptyStatHint}>Complete your first!</span>}</span>
                      </div>
                    </div>
                    <div className={styles.statItem}>
                      <Calendar className={styles.statIcon} size={24} />
                      <div className={styles.statInfo}>
                        <span className={styles.statLabel}>Upcoming Events</span>
                        <span className={styles.statValue}>{userEvents.length}</span>
                      </div>
                    </div>
                    <div className={styles.statItem}>
                      <Briefcase className={styles.statIcon} size={24} />
                      <div className={styles.statInfo}>
                        <span className={styles.statLabel}>Active Positions</span>
                        <span className={styles.statValue}>
                          {[user?.primaryPosition, user?.secondaryPosition, user?.tertiaryPosition].filter(Boolean).length}
                        </span>
                      </div>
                    </div>
                    <div className={styles.statItem}>
                      <Clock className={styles.statIcon} size={24} />
                      <div className={styles.statInfo}>
                        <span className={styles.statLabel}>Last Login</span>
                        <span className={styles.statValue} style={{ fontSize: '16px' }}>
                          {lastLogin ? new Date(lastLogin).toLocaleDateString() : 'Welcome!'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions Widget */}
                <div className={styles.widget}>
                  <div className={styles.widgetHeader}>
                    <Zap size={18} style={{ marginRight: '0.5rem' }} />
                    <h3 className={styles.widgetTitle}>Quick Actions</h3>
                  </div>
                  <div className={styles.widgetContent}>
                    {user?.flags?.includes('OrgMember') && (
                      <>
                        <div className={styles.quickActionsGroup}>
                          <p className={styles.quickActionsGroupLabel}>Organization</p>
                          <div className={styles.quickActionsList}>
                            <button className={styles.quickActionButton} onClick={() => window.location.href = '/events'}>
                              <Calendar size={20} />
                              <span>View Events</span>
                            </button>
                            <button className={styles.quickActionButton} onClick={() => window.location.href = '/missions'}>
                              <ScrollText size={20} />
                              <span>Browse Missions</span>
                            </button>
                            <button className={styles.quickActionButton} onClick={() => window.location.href = '/departments'}>
                              <Briefcase size={20} />
                              <span>Departments</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                    <div className={styles.quickActionsGroup}>
                      <p className={styles.quickActionsGroupLabel}>Personal</p>
                      <div className={styles.quickActionsList}>
                        <button className={styles.quickActionButton} onClick={() => setActiveTab('profile')}>
                          <User size={20} />
                          <span>Edit Profile</span>
                        </button>
                        <button className={styles.quickActionButton} onClick={() => setShowModal(true)}>
                          <Rocket size={20} />
                          <span>Preferences</span>
                        </button>
                        <button className={styles.quickActionButton} onClick={() => setActiveTab('settings')}>
                          <Settings size={20} />
                          <span>Settings</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Discovery Widget */}
                <FeatureDiscoveryWidget 
                  orgPoints={orgPoints}
                  userFlags={user?.flags || []}
                  userRole={user?.role || ''}
                />

                {/* Recent Activity Widget */}
                <div className={styles.widget}>
                  <div className={styles.widgetHeader}>
                    <Activity size={18} style={{ marginRight: '0.5rem' }} />
                    <h3 className={styles.widgetTitle}>Recent Activity</h3>
                  </div>
                  <div className={styles.widgetContent}>
                    {recentActivity.length === 0 ? (
                      <p className={styles.emptyState}>Start your journey by completing the checklist below!</p>
                    ) : (
                      <div className={styles.activityList}>
                        {recentActivity.map((activity, idx) => (
                          <div key={idx} className={styles.activityItem}>
                            {activity.icon === 'calendar' && <Calendar size={18} className={styles.activityIcon} />}
                            {activity.icon === 'check' && <CheckCircle size={18} className={styles.activityIcon} />}
                            {activity.icon === 'coins' && <Coins size={18} className={styles.activityIcon} />}
                            <div className={styles.activityDetails}>
                              <span className={styles.activityTitle}>{activity.title}</span>
                              <span className={styles.activityTime}>{activity.time}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Upcoming Events Widget */}
                <div className={styles.widget}>
                  <div className={styles.widgetHeader}>
                    <Calendar size={18} style={{ marginRight: '0.5rem' }} />
                    <h3 className={styles.widgetTitle}>Upcoming Events</h3>
                  </div>
                  <div className={styles.widgetContent}>
                    {userEvents.length === 0 ? (
                      <p className={styles.emptyState}>No upcoming events. Check the Events page to RSVP!</p>
                    ) : (
                      <div className={styles.activityList}>
                        {userEvents.slice(0, 3).map((event) => (
                          <div key={event.id} className={styles.activityItem}>
                            <Calendar size={18} className={styles.activityIcon} />
                            <div className={styles.activityDetails}>
                              <span className={styles.activityTitle}>{event.title}</span>
                              <span className={styles.activityTime}>
                                {new Date(event.start).toLocaleDateString()} • {getTimeUntilEvent(event.start)}
                              </span>
                            </div>
                          </div>
                        ))}
                        {userEvents.length > 3 && (
                          <button className={styles.viewAllButton} onClick={() => setActiveTab('events')}>
                            View all {userEvents.length} events →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* What's New Widget */}
                <div className={styles.widget}>
                  <div className={styles.widgetHeader}>
                    <Bell size={18} style={{ marginRight: '0.5rem' }} />
                    <h3 className={styles.widgetTitle}>What's New</h3>
                  </div>
                  <div className={styles.widgetContent}>
                    <div className={styles.announcementsList}>
                      <div className={styles.announcementItem}>
                        <div className={styles.announcementDot}></div>
                        <div className={styles.announcementContent}>
                          <span className={styles.announcementTitle}>v1.6 - Mission System Live!</span>
                          <span className={styles.announcementText}>Complete missions, track progress, and earn rewards</span>
                        </div>
                      </div>
                      <div className={styles.announcementItem}>
                        <div className={styles.announcementDot}></div>
                        <div className={styles.announcementContent}>
                          <span className={styles.announcementTitle}>Member Directory</span>
                          <span className={styles.announcementText}>Browse all org members with search and filters</span>
                        </div>
                      </div>
                      <div className={styles.announcementItem}>
                        <div className={styles.announcementDot}></div>
                        <div className={styles.announcementContent}>
                          <span className={styles.announcementTitle}>Dashboard Improvements</span>
                          <span className={styles.announcementText}>New overview tab with quick stats and actions</span>
                        </div>
                      </div>
                    </div>
                    <button className={styles.viewAllButton} onClick={() => window.location.href = '/changelog'}>
                      View Full Changelog →
                    </button>
                  </div>
                </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
        <div className={styles.cardGrid}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>User Information</h2>

            <div className={styles.field}>
              <strong>Name:</strong>{' '}
              {isEditing ? (
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              ) : (
                <span className={styles.usernameValue}>{user.username}</span>
              )}
            </div>

            <div className={styles.field}>
              <strong>Game Username:</strong>{' '}
              {isEditing ? (
                <input
                  type="text"
                  value={gameUserName}
                  onChange={(e) => setGameUserName(e.target.value)}
                />
              ) : (
                user.gameUserName || 'N/A'
              )}
            </div>
            {/* Add your message here */}
            <div className={styles.field}>
              <p className={styles.infoMessage}>Please make sure your Discord (server name) name is accurate as it will be used for event role assignments.</p>
            </div>
            <div className={styles.field}>
            <strong>Discord Name:</strong>{' '}
              {isEditing
                ? <input
                    type="text"
                    value={discordName}
                    onChange={e => setDiscordName(e.target.value)}
                  />
                : (user.discordName || 'N/A')
              }
            </div>

            {/* Level-based Profile Enhancements */}
            {getLevel(orgPoints).level >= 3 && (
              <div className={styles.field}>
                <strong>
                  Motto/Specialization: 
                  <Tooltip content="Unlocked at Level 3+. Share your personal motto or specialization (max 200 chars)">
                    <HelpCircle size={14} style={{ marginLeft: '4px', verticalAlign: 'middle', opacity: 0.6 }} />
                  </Tooltip>
                </strong>
                {isEditing ? (
                  <input
                    type="text"
                    value={motto}
                    onChange={(e) => setMotto(e.target.value.slice(0, 200))}
                    placeholder="e.g., 'Expert Cargo Runner' or 'First In, Last Out'"
                    maxLength={200}
                  />
                ) : (
                  <span>{motto || 'Not set'}</span>
                )}
              </div>
            )}

            {getLevel(orgPoints).level >= 5 && (
              <div className={styles.field}>
                <strong>
                  Elite Mission History: 
                  <Tooltip content="Unlocked at Level 5+. Toggle to show/hide your elite mission history">
                    <HelpCircle size={14} style={{ marginLeft: '4px', verticalAlign: 'middle', opacity: 0.6 }} />
                  </Tooltip>
                </strong>
                {isEditing ? (
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={showEliteLogs}
                      onChange={(e) => setShowEliteLogs(e.target.checked)}
                    />
                    <span>Show Elite Mission Logs</span>
                  </label>
                ) : (
                  <span>{showEliteLogs ? 'Enabled' : 'Disabled'}</span>
                )}
              </div>
            )}

            {getLevel(orgPoints).level >= 6 && (
              <div className={styles.field}>
                <strong>
                  Profile Theme: 
                  <Tooltip content="Unlocked at Level 6+. Customize your profile appearance">
                    <HelpCircle size={14} style={{ marginLeft: '4px', verticalAlign: 'middle', opacity: 0.6 }} />
                  </Tooltip>
                </strong>
                {isEditing ? (
                  <select
                    value={profileTheme}
                    onChange={(e) => setProfileTheme(e.target.value)}
                    className={styles.themeSelect}
                  >
                    <option value="">Default</option>
                    <option value="elite-blue">Elite Blue</option>
                    <option value="master-gold">Master Gold</option>
                    <option value="legend-purple">Legend Purple</option>
                  </select>
                ) : (
                  <span>{profileTheme || 'Default'}</span>
                )}
              </div>
            )}

            {getLevel(orgPoints).level >= 10 && (
              <div className={styles.field}>
                <strong>
                  Paragon Biography: 
                  <Tooltip content="Unlocked at Level 10 (Paragon). Write an extended biography for the Paragon Hall">
                    <HelpCircle size={14} style={{ marginLeft: '4px', verticalAlign: 'middle', opacity: 0.6 }} />
                  </Tooltip>
                </strong>
                {isEditing ? (
                  <textarea
                    value={paragonBio}
                    onChange={(e) => setParagonBio(e.target.value)}
                    placeholder="Share your journey, achievements, and advice for new members..."
                    className={styles.paragonBioTextarea}
                    rows={6}
                  />
                ) : (
                  <p className={styles.paragonBioText}>{paragonBio || 'Not set'}</p>
                )}
              </div>
            )}

            {/* Timezone and Availability */}
            <div className={styles.field}>
              <strong>
                Timezone: 
                <Tooltip content="Select your timezone for coordinating events and missions">
                  <HelpCircle size={14} style={{ marginLeft: '4px', verticalAlign: 'middle', opacity: 0.6 }} />
                </Tooltip>
              </strong>
              {isEditing ? (
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className={styles.timezoneSelect}
                >
                  <option value="">Select Timezone</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Anchorage">Alaska Time (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                  <option value="UTC">UTC</option>
                  <option value="Europe/London">London (GMT/BST)</option>
                  <option value="Europe/Paris">Central Europe (CET/CEST)</option>
                  <option value="Europe/Athens">Eastern Europe (EET/EEST)</option>
                  <option value="Asia/Dubai">Dubai (GST)</option>
                  <option value="Asia/Kolkata">India (IST)</option>
                  <option value="Asia/Shanghai">China (CST)</option>
                  <option value="Asia/Tokyo">Japan (JST)</option>
                  <option value="Australia/Sydney">Sydney (AEDT/AEST)</option>
                  <option value="Pacific/Auckland">New Zealand (NZDT/NZST)</option>
                </select>
              ) : (
                <span>{timezone || 'Not set'}</span>
              )}
            </div>

            {isEditing && (
              <div className={styles.field}>
                <strong>
                  Preferred Play Times: 
                  <Tooltip content="Set your typical availability for each day of the week">
                    <HelpCircle size={14} style={{ marginLeft: '4px', verticalAlign: 'middle', opacity: 0.6 }} />
                  </Tooltip>
                </strong>
                <div className={styles.playtimeSchedule}>
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => {
                    const dayData = preferredPlaytimes[day] || { start: '18:00', end: '22:00', available: false };
                    return (
                      <div key={day} className={styles.playtimeDay}>
                        <label className={styles.dayLabel}>
                          <input
                            type="checkbox"
                            checked={dayData.available}
                            onChange={(e) => {
                              setPreferredPlaytimes({
                                ...preferredPlaytimes,
                                [day]: { ...dayData, available: e.target.checked }
                              });
                            }}
                          />
                          <span className={styles.dayName}>{day}</span>
                        </label>
                        {dayData.available && (
                          <div className={styles.timeInputs}>
                            <input
                              type="time"
                              value={dayData.start}
                              onChange={(e) => {
                                setPreferredPlaytimes({
                                  ...preferredPlaytimes,
                                  [day]: { ...dayData, start: e.target.value }
                                });
                              }}
                              className={styles.timeInput}
                            />
                            <span className={styles.timeSeparator}>to</span>
                            <input
                              type="time"
                              value={dayData.end}
                              onChange={(e) => {
                                setPreferredPlaytimes({
                                  ...preferredPlaytimes,
                                  [day]: { ...dayData, end: e.target.value }
                                });
                              }}
                              className={styles.timeInput}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!isEditing && Object.keys(preferredPlaytimes).filter(day => preferredPlaytimes[day]?.available).length > 0 && (
              <div className={styles.field}>
                <strong>Preferred Play Times:</strong>
                <div className={styles.playtimeSummary}>
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
                    .filter(day => preferredPlaytimes[day]?.available)
                    .map(day => {
                      const dayData = preferredPlaytimes[day];
                      return (
                        <div key={day} className={styles.playtimeItem}>
                          <strong>{day}:</strong> {dayData.start} - {dayData.end}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Add your message here */}
            <div className={styles.field}>
              <p className={styles.infoMessage}>Please make sure your email is valid, as it is used for password reset requests.</p>
            </div>
            <div className={styles.field}>
              <strong>Email:</strong> {user.email}
            </div>
            {/* Add your message here */}
            <div className={styles.field}>
              <p className={styles.infoMessage}>Based on your set preferences below, these are your officially assigned positions by the leadership team</p>
            </div>
            <div className={styles.field}>
              <strong>Primary Position:</strong> {user.primaryPositionTitle || 'None'}
            </div>
            <div className={styles.field}>
              <strong>Secondary Position:</strong> {user.secondaryPositionTitle || 'None'}
            </div>
            <div className={styles.field}>
              <strong>Tertiary Position:</strong> {user.tertiaryPositionTitle || 'None'}
            </div>
            <div className={styles.field}>
              <strong>Last Login:</strong>{' '}
              {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
            </div>
            <div className={styles.field}>
              <strong>Join Date:</strong>{' '}
              {user.joinDate ? new Date(user.joinDate).toLocaleDateString() : 'Unknown'}
            </div>
              {/* Add Account Role here */}
              <div className={styles.field}>
              <strong>Account Role:</strong> {getUserRole(user?.role)}
              </div>
            {isEditing && (
              <div className={styles.field}>
                <strong>Avatar:</strong>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
                />
                <button onClick={handleAvatarUpload}>Upload Avatar</button>
              </div>
            )}
            <div className={styles.buttonRow}>
              {isEditing ? (
                <>
                  <button className="saveButton" onClick={handleSave}>Save</button>
                  <button className="cancelButton" onClick={() => setIsEditing(false)}>Cancel</button>
                  <button
                    className="deleteButton"
                    onClick={handleDeleteAccount}
                  >
                    Delete Account
                  </button>
                </>
              ) : (
                <button className="editButton" onClick={() => setIsEditing(true)}>Edit Profile</button>
              )}
            </div>
          </div>
        </div>
          )}

          {activeTab === 'journey' && (
        <div className={`${styles.card} ${styles.marginTopLarge}`}>
          <h2 className={styles.cardTitle}>My Org Journey</h2>

          <div className={styles.field}>
            <strong>Primary Interests:</strong>
            <span>{orgJourney?.interests?.join(', ') || 'None selected'}</span>
          </div>

          <div className={styles.field}>
            <strong>Playstyle:</strong>
            <span>{orgJourney?.playstyle || 'None selected'}</span>
          </div>

          <div className={styles.field}>
            <strong>Squad Preference:</strong>
            <span>{orgJourney?.squadPreference || 'None selected'}</span>
          </div>

          <div className={styles.field}>
            <strong>Skills / Contributions:</strong>
            <span>{orgJourney?.skills || 'None selected'}</span>
          </div>
          <div className={styles.field}>
            <strong>Primary Role Preference:</strong>
            <span>{orgJourney?.primaryRole || 'None selected'}</span>
          </div>

          <div className={styles.field}>
            <strong>Secondary Role Preference:</strong>
            <span>{orgJourney?.secondaryRole || 'None selected'}</span>
          </div>

          <div className={styles.field}>
            <strong>Tertiary Role Preference:</strong>
            <span>{orgJourney?.tertiaryRole || 'None selected'}</span>
          </div>
          <div className={styles.buttonRow}>
          <button className={styles.editButton} onClick={() => setShowModal(true)}>
            Update Preferences
          </button>
          </div>
        </div>
          )}

          {/* Commendations Card - Show in Profile Tab */}
          {activeTab === 'profile' && getLevel(orgPoints).level >= 1 && (
            <div className={`${styles.card} ${styles.marginTopLarge}`} style={{ maxWidth: '100%', position: 'relative' }}>
              {commendationsCount > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '2rem',
                  right: '2rem',
                  padding: '0.625rem 1.25rem',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  borderRadius: '9999px',
                  fontSize: '0.9375rem',
                  fontWeight: '700',
                  color: '#ffffff',
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                  letterSpacing: '0.025em',
                  whiteSpace: 'nowrap',
                  zIndex: 10
                }}>
                  {commendationsCount} earned
                </div>
              )}
              <h2 className={styles.cardTitle}>My Commendations</h2>
              <p className={styles.sectionDescription}>
                Your earned commendations recognizing outstanding achievements and contributions to TFIC.
              </p>
              <CommendationsDisplay memberId={user.id} onCountChange={setCommendationsCount} />
            </div>
          )}

          {activeTab === 'positions' && (
        <div className={`${styles.card} ${styles.marginTopLarge}`}>
          <h2 className={styles.cardTitle}>My Positions</h2>
          <p className={styles.sectionDescription}>
            These are your currently assigned positions within the organization. Each role comes with responsibilities and access levels relevant to your department. Finally, you can only ever hold 3 positions max at any given time.

            You're current assigned positions:
          </p>
          <div className={styles.positionCardGroup}>
            {/* Primary Position */}
            <div className={styles.positionCard}>
              <div className={styles.positionHeader}>Primary</div>
              <div className={styles.positionTitle}>{user.primaryPositionTitle || 'None'}</div>
              <div className={styles.positionDescription}>
                {user.primaryPositionTitle
                  ? positionInfo.find(pos => pos.title === user.primaryPositionTitle)?.description || 'No description available.'
                  : 'None'}
              </div>
            </div>

            {/* Secondary Position */}
            <div className={styles.positionCard}>
              <div className={styles.positionHeader}>Secondary</div>
              <div className={styles.positionTitle}>{user.secondaryPositionTitle || 'None'}</div>
              <div className={styles.positionDescription}>
                {user.secondaryPositionTitle
                  ? positionInfo.find(pos => pos.title === user.secondaryPositionTitle)?.description || 'No description available.'
                  : 'None'}
              </div>
            </div>
            {/* Tertiary Position */}
            <div className={styles.positionCard}>
              <div className={styles.positionHeader}>Tertiary</div>
              <div className={styles.positionTitle}>{user.tertiaryPositionTitle || 'None'}</div>
              <div className={styles.positionDescription}>
                {user.tertiaryPositionTitle
                  ? positionInfo.find(pos => pos.title === user.tertiaryPositionTitle)?.description || 'No description available.'
                  : 'None'}
              </div>
            </div>
          </div>
        </div>
          )}

          {activeTab === 'events' && (
        <div className={`${styles.card} ${styles.marginTopLarge}`}>
          <h2 className={styles.cardTitle}>My RSVP'd Events</h2>

          {userEvents.length === 0 ? (
            <p className={styles.sectionDescription}>You haven’t RSVP’d to any events yet.</p>
          ) : (
            <ul className={styles.eventList}>
            {userEvents.map((ev) => (
              <li key={ev.id} className={styles.eventCard}>
                <div className={styles.eventContent}>
                  {ev.eventImageUrl ? (
                    <div className={styles.eventImageContainer}>
                      <img
                        src={ev.eventImageUrl}
                        alt=""
                        className={styles.eventCardImage}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <div className={styles.eventImageContainer}>
                      <div className={styles.eventCardImagePlaceholder}>
                        📅
                      </div>
                    </div>
                  )}
                  <div className={styles.eventDetails}>
                    <h3 className={styles.eventTitle}>{ev.title}</h3>
                    <p className={styles.eventTime}>
                      {new Date(ev.start).toLocaleDateString()} —{' '}
                      {new Date(ev.start).toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                      {' '}({getTimeUntilEvent(ev.start)})
                    </p>

                    {ev.role && (
                      <p className={styles.eventMeta}>
                        <strong>Role:</strong> {ev.role}
                      </p>
                    )}

                    {ev.description && (
                      <div
                        className={styles.eventDescription}
                        dangerouslySetInnerHTML={{ __html: ev.description }}
                      />
                    )}

                    {/* ✅ CANCEL BUTTON */}
                    {ev.role && (
                    <div className={styles.eventActions}>
                      <button
                        className={styles.cancelRsvpButton}
                        onClick={() =>
                          handleCancelRsvp(
                            ev.id,
                            ev.role,
                            ev.discordChannelId,
                            ev.discordMessageId
                          )
                        }
                      >
                        Cancel RSVP
                      </button>
                    </div>
                  )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          )}
        </div>
          )}

          {activeTab === 'missions' && (
            <div className={`${styles.card} ${styles.marginTopLarge}`}>
              <h2 className={styles.cardTitle}>Mission History</h2>

              {missionHistory.length === 0 ? (
                <p className={styles.sectionDescription}>You haven't completed any missions yet.</p>
              ) : (
                <div className={styles.missionHistoryList}>
                  {missionHistory.map((mission) => (
                    <div key={mission.missionId} className={styles.missionHistoryCard}>
                      <div className={styles.missionHistoryHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <h3 className={styles.missionHistoryTitle}>{mission.missionTitle}</h3>
                          <LevelBadge level={getLevel(mission.rewardPoints || 0).level} size="small" />
                        </div>
                        <span className={styles.missionHistoryStatus}>✓ Completed</span>
                      </div>
                      <p className={styles.missionHistoryObjective}>{mission.missionObjective}</p>
                      <div className={styles.missionHistoryMeta}>
                        <div className={styles.missionHistoryMetaItem}>
                          <Calendar size={16} />
                          <span>Completed: {new Date(mission.completedAt).toLocaleDateString()}</span>
                        </div>
                        {mission.rewardPoints && (
                          <div className={styles.missionHistoryReward}>
                            <Coins size={16} />
                            <span>+{mission.rewardPoints} ORG POINTS</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <ThemeSelector />
            </div>
          )}
        </div>
      </div>
      {showModal && (
      <PreferencesModal
        initial={orgJourney}
        onSave={(updated) => {
          setOrgJourney(updated);
          setShowModal(false);
      
          axios.put('/auth/user/preferences', updated)
            .catch(err => console.error('Failed to save preferences', err));
        }}
        onClose={() => setShowModal(false)}
      />
    )}

      {/* Code of Conduct Modal */}
      {showCodeOfConductModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCodeOfConductModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Code of Conduct 🛡️</h2>
              <button className={styles.closeButton} onClick={() => setShowCodeOfConductModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <h3>Welcome to TFIC!</h3>
              <p>Our Code of Conduct ensures a respectful and inclusive environment for all members. Please read and understand these guidelines:</p>
              
              <h4>1. Respect All Members</h4>
              <p>Treat everyone with dignity and respect. Harassment, discrimination, hate speech, or personal attacks will not be tolerated.</p>
              
              <h4>2. Communication Standards</h4>
              <p>Keep discussions constructive and professional. Excessive profanity, spam, or disruptive behavior is prohibited.</p>
              
              <h4>3. Gaming Conduct</h4>
              <p>Play fair and follow game rules. Cheating, exploiting, or griefing other players is strictly forbidden.</p>
              
              <h4>4. Confidentiality</h4>
              <p>Respect privacy and don't share personal information without consent. Internal org discussions should remain confidential.</p>
              
              <h4>5. Leadership & Hierarchy</h4>
              <p>Follow chain of command and respect leadership decisions. Disagreements should be handled professionally through proper channels.</p>
              
              <h4>6. Content Guidelines</h4>
              <p>Keep all content appropriate for all ages. No NSFW, illegal, or controversial political/religious content.</p>
              
              <h4>7. Account Security</h4>
              <p>Keep your account secure. Account sharing or impersonation is prohibited.</p>
              
              <h4>8. Reporting Issues</h4>
              <p>Report violations to leadership immediately. False reports or retaliation against reporters will result in disciplinary action.</p>
              
              <p><strong>Violations may result in warnings, suspension, or permanent removal from the organization.</strong></p>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.acceptButton} 
                onClick={() => {
                  handleChecklistToggle('codeOfConduct');
                  setShowCodeOfConductModal(false);
                }}
              >
                I Accept the Code of Conduct
              </button>
              <button className={styles.cancelButton} onClick={() => setShowCodeOfConductModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
  </div>
  );
};

export default Dashboard;
